<template>
  <AdminLayout>
    <div class="max-w-4xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">HOT 분양 AD 관리</h1>
          <p class="text-sm text-slate-500">HOT 분양현장 섹션의 첫 번째 AD 슬롯을 관리합니다</p>
        </div>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">로딩 중...</p>
      </div>

      <!-- 현재 AD 상태 -->
      <div v-else class="space-y-6">
        <!-- 현재 설정된 AD -->
        <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div class="p-5 border-b border-slate-100">
            <h2 class="text-lg font-semibold text-slate-800">현재 AD 설정</h2>
          </div>

          <div v-if="currentAd" class="p-5">
            <div class="bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
              <div class="flex items-start justify-between mb-3">
                <span class="px-2 py-0.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded">
                  AD
                </span>
                <span class="text-xs text-slate-500">
                  만료: {{ currentAd.expiresAt ? formatDate(currentAd.expiresAt) : '무기한' }}
                </span>
              </div>
              <h3 class="text-lg font-semibold text-slate-800 mb-1">{{ currentAd.propertyName }}</h3>
              <p class="text-sm text-slate-500 mb-3">{{ currentAd.address }}</p>
              <div class="flex items-center gap-2 text-sm">
                <span class="px-2 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">
                  캠페인: {{ currentAd.campaignCode }}
                </span>
              </div>
            </div>

            <div class="flex gap-2 mt-4">
              <button
                @click="showEditModal = true"
                class="flex-1 py-2 border border-blue-500 text-blue-600 rounded-xl hover:bg-blue-50 transition"
              >
                수정하기
              </button>
              <button
                @click="handleDeleteAd"
                :disabled="deleting"
                class="flex-1 py-2 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition disabled:opacity-50"
              >
                {{ deleting ? '삭제 중...' : '삭제하기' }}
              </button>
            </div>
          </div>

          <div v-else class="p-8 text-center">
            <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span class="text-2xl opacity-50">🔥</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-600 mb-2">설정된 AD가 없습니다</h3>
            <p class="text-slate-400 mb-4">활성 캠페인 중 하나를 선택하여 HOT AD로 등록하세요.</p>
            <button
              @click="showEditModal = true"
              class="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              AD 등록하기
            </button>
          </div>
        </div>

        <!-- 안내 -->
        <div class="bg-slate-50 rounded-2xl p-5">
          <h3 class="font-semibold text-slate-700 mb-2">HOT AD란?</h3>
          <ul class="text-sm text-slate-500 space-y-1">
            <li>• HOT 분양현장 섹션의 <strong>첫 번째 슬롯</strong>에 AD 배지와 함께 노출됩니다.</li>
            <li>• 활성 상태인 캠페인 중 하나를 선택하여 연결합니다.</li>
            <li>• AD 카드 클릭 시 해당 캠페인 랜딩 페이지로 이동합니다.</li>
            <li>• 나머지 슬롯은 상담 신청 수 기준으로 자동 정렬됩니다.</li>
          </ul>
        </div>
      </div>

      <!-- AD 등록/수정 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showEditModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">
                {{ currentAd ? 'AD 수정' : 'AD 등록' }}
              </h3>

              <!-- 캠페인 로딩 -->
              <div v-if="loadingCampaigns" class="py-8 text-center">
                <div class="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p class="text-slate-500">캠페인 목록 로딩 중...</p>
              </div>

              <!-- 캠페인 없음 -->
              <div v-else-if="activeCampaigns.length === 0" class="py-8 text-center">
                <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h4 class="font-semibold text-slate-600 mb-2">활성 캠페인이 없습니다</h4>
                <p class="text-sm text-slate-400">파트너가 캠페인을 생성하면 여기에 표시됩니다.</p>
                <button
                  @click="showEditModal = false"
                  class="mt-4 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  닫기
                </button>
              </div>

              <!-- 캠페인 선택 -->
              <div v-else>
                <label class="block text-sm font-medium text-slate-700 mb-2">캠페인 선택</label>
                <div class="space-y-2 max-h-80 overflow-y-auto mb-4">
                  <label
                    v-for="campaign in activeCampaigns"
                    :key="campaign.id"
                    :class="[
                      'flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition',
                      selectedCampaignId === campaign.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'
                    ]"
                  >
                    <input
                      type="radio"
                      :value="campaign.id"
                      v-model="selectedCampaignId"
                      class="w-4 h-4 text-orange-500"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="font-medium text-slate-800 truncate">{{ campaign.propertyName }}</span>
                        <span class="text-xs text-slate-400 font-mono">{{ campaign.code }}</span>
                      </div>
                      <div class="text-xs text-slate-500 mt-0.5">
                        {{ campaign.partnerDisplayName }} · {{ campaign.durationLabel }} · 종료 {{ campaign.endDate }}
                      </div>
                    </div>
                  </label>
                </div>

                <!-- 버튼 -->
                <div class="flex gap-2">
                  <button
                    type="button"
                    @click="showEditModal = false"
                    class="flex-1 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
                  >
                    취소
                  </button>
                  <button
                    @click="handleSaveAd"
                    :disabled="!selectedCampaignId || saving"
                    class="flex-1 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition disabled:opacity-50"
                  >
                    {{ saving ? '저장 중...' : (currentAd ? '수정하기' : '등록하기') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useHotProperty } from '@/composables/main/useHotProperty'

export default {
  name: 'HotAdManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { getHotAd, setHotAd, deleteHotAd, getActiveCampaigns } = useHotProperty()

    const loading = ref(true)
    const currentAd = ref(null)
    const showEditModal = ref(false)
    const loadingCampaigns = ref(false)
    const activeCampaigns = ref([])
    const selectedCampaignId = ref(null)
    const saving = ref(false)
    const deleting = ref(false)

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    const loadCurrentAd = async () => {
      try {
        loading.value = true
        currentAd.value = await getHotAd()
      } catch (error) {
        console.error('Hot AD 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const loadCampaigns = async () => {
      try {
        loadingCampaigns.value = true
        activeCampaigns.value = await getActiveCampaigns()
      } catch (error) {
        console.error('캠페인 로드 실패:', error)
      } finally {
        loadingCampaigns.value = false
      }
    }

    const handleSaveAd = async () => {
      if (!selectedCampaignId.value) return

      const selectedCampaign = activeCampaigns.value.find(c => c.id === selectedCampaignId.value)
      if (!selectedCampaign) return

      saving.value = true
      try {
        const adData = {
          campaignId: selectedCampaign.id,
          campaignCode: selectedCampaign.code,
          propertyId: selectedCampaign.propertyId,
          propertyName: selectedCampaign.propertyName,
          partnerId: selectedCampaign.partnerId,
          partnerDisplayName: selectedCampaign.partnerDisplayName,
          address: selectedCampaign.propertyAddress || '',
          expiresAt: selectedCampaign.expiresAt,
          createdAt: currentAd.value?.createdAt || new Date().toISOString()
        }

        const success = await setHotAd(adData)
        if (success) {
          alert('AD가 저장되었습니다.')
          showEditModal.value = false
          await loadCurrentAd()
        } else {
          alert('저장에 실패했습니다.')
        }
      } catch (error) {
        console.error('AD 저장 실패:', error)
        alert('저장에 실패했습니다: ' + error.message)
      } finally {
        saving.value = false
      }
    }

    const handleDeleteAd = async () => {
      if (!confirm('현재 설정된 AD를 삭제하시겠습니까?')) return

      deleting.value = true
      try {
        const success = await deleteHotAd()
        if (success) {
          alert('AD가 삭제되었습니다.')
          currentAd.value = null
        } else {
          alert('삭제에 실패했습니다.')
        }
      } catch (error) {
        console.error('AD 삭제 실패:', error)
        alert('삭제에 실패했습니다: ' + error.message)
      } finally {
        deleting.value = false
      }
    }

    // 모달 열릴 때 캠페인 로드
    watch(showEditModal, async (newVal) => {
      if (newVal) {
        await loadCampaigns()
        // 현재 설정된 캠페인 선택
        if (currentAd.value?.campaignId) {
          selectedCampaignId.value = currentAd.value.campaignId
        } else {
          selectedCampaignId.value = null
        }
      }
    })

    onMounted(loadCurrentAd)

    return {
      loading,
      currentAd,
      showEditModal,
      loadingCampaigns,
      activeCampaigns,
      selectedCampaignId,
      saving,
      deleting,
      formatDate,
      handleSaveAd,
      handleDeleteAd
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
